/**
 * @fileoverview Performance Monitoring Hook
 * @description React hook for component-level performance monitoring and optimization
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { performanceMonitor, memoryMonitor } from '../utils/performance';

/**
 * Hook for performance monitoring
 * @param {string} componentName - Name of the component
 * @param {Object} options - Configuration options
 * @returns {Object} Performance utilities and metrics
 */
export function usePerformance(componentName, options = {}) {
  const {
    trackMount = true,
    trackRender = false,
    trackMemory = false,
    memoryInterval = 5000,
    logMetrics = process.env.NODE_ENV === 'development'
  } = options;

  const [metrics, setMetrics] = useState({});
  const [memoryUsage, setMemoryUsage] = useState(null);
  const renderCount = useRef(0);
  const memoryIntervalId = useRef(null);

  // Track component mount time
  useEffect(() => {
    if (trackMount) {
      performanceMonitor.startTiming(`${componentName}-mount`);
      
      return () => {
        const mountTime = performanceMonitor.endTiming(`${componentName}-mount`);
        if (logMetrics) {
          console.log(`${componentName} mount time: ${mountTime}ms`);
        }
        setMetrics(prev => ({ ...prev, mountTime }));
      };
    }
  }, [componentName, trackMount, logMetrics]);

  // Track render count and time
  useEffect(() => {
    if (trackRender) {
      renderCount.current += 1;
      const renderStart = performance.now();
      
      // Use setTimeout to measure render time
      setTimeout(() => {
        const renderTime = performance.now() - renderStart;
        if (logMetrics) {
          console.log(`${componentName} render #${renderCount.current}: ${renderTime}ms`);
        }
        setMetrics(prev => ({
          ...prev,
          renderTime,
          renderCount: renderCount.current
        }));
      }, 0);
    }
  });

  // Track memory usage
  useEffect(() => {
    if (trackMemory) {
      memoryIntervalId.current = memoryMonitor.startMonitoring((usage) => {
        setMemoryUsage(usage);
        if (logMetrics && usage.percentage > 80) {
          console.warn(`${componentName} high memory usage: ${usage.percentage}%`);
        }
      }, memoryInterval);

      return () => {
        if (memoryIntervalId.current) {
          memoryMonitor.stopMonitoring(memoryIntervalId.current);
        }
      };
    }
  }, [componentName, trackMemory, memoryInterval, logMetrics]);

  // Utility functions
  const startTiming = useCallback((name) => {
    performanceMonitor.startTiming(`${componentName}-${name}`);
  }, [componentName]);

  const endTiming = useCallback((name) => {
    const duration = performanceMonitor.endTiming(`${componentName}-${name}`);
    if (logMetrics) {
      console.log(`${componentName} ${name}: ${duration}ms`);
    }
    setMetrics(prev => ({ ...prev, [name]: duration }));
    return duration;
  }, [componentName, logMetrics]);

  const measureAsync = useCallback(async (name, asyncFn) => {
    startTiming(name);
    try {
      const result = await asyncFn();
      endTiming(name);
      return result;
    } catch (error) {
      endTiming(name);
      throw error;
    }
  }, [startTiming, endTiming]);

  return {
    metrics,
    memoryUsage,
    startTiming,
    endTiming,
    measureAsync,
    renderCount: renderCount.current
  };
}

/**
 * Hook for lazy loading images
 * @returns {Object} Lazy loading utilities
 */
export function useLazyImage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if ('IntersectionObserver' in window) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.unobserve(img);
          }
        },
        { rootMargin: '50px 0px', threshold: 0.01 }
      );

      observerRef.current.observe(img);
    } else {
      // Fallback for browsers without IntersectionObserver
      setIsInView(true);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    console.error('Failed to load image');
  }, []);

  return {
    imgRef,
    isLoaded,
    isInView,
    handleLoad,
    handleError
  };
}

/**
 * Hook for debounced values
 * @param {any} value - Value to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {any} Debounced value
 */
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook for throttled callbacks
 * @param {Function} callback - Callback function
 * @param {number} delay - Throttle delay in ms
 * @returns {Function} Throttled callback
 */
export function useThrottle(callback, delay) {
  const throttledCallback = useRef(null);
  const lastRan = useRef(Date.now());

  useEffect(() => {
    throttledCallback.current = callback;
  }, [callback]);

  return useCallback((...args) => {
    if (Date.now() - lastRan.current >= delay) {
      throttledCallback.current?.(...args);
      lastRan.current = Date.now();
    }
  }, [delay]);
}

/**
 * Hook for measuring component performance
 * @param {string} componentName - Name of the component
 * @returns {Object} Performance measurement utilities
 */
export function useComponentPerformance(componentName) {
  const [performanceData, setPerformanceData] = useState({
    mountTime: 0,
    updateTime: 0,
    renderCount: 0
  });

  const mountStartTime = useRef(null);
  const updateStartTime = useRef(null);
  const renderCount = useRef(0);

  // Track mount time
  useEffect(() => {
    mountStartTime.current = performance.now();
    
    return () => {
      if (mountStartTime.current) {
        const mountTime = performance.now() - mountStartTime.current;
        setPerformanceData(prev => ({ ...prev, mountTime }));
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`${componentName} total mount time: ${mountTime.toFixed(2)}ms`);
        }
      }
    };
  }, [componentName]);

  // Track render count and update time
  useEffect(() => {
    renderCount.current += 1;
    
    if (renderCount.current > 1) {
      updateStartTime.current = performance.now();
      
      setTimeout(() => {
        if (updateStartTime.current) {
          const updateTime = performance.now() - updateStartTime.current;
          setPerformanceData(prev => ({
            ...prev,
            updateTime,
            renderCount: renderCount.current
          }));
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`${componentName} update #${renderCount.current - 1}: ${updateTime.toFixed(2)}ms`);
          }
        }
      }, 0);
    }
  });

  return performanceData;
}

/**
 * Hook for virtual scrolling performance optimization
 * @param {Array} items - Array of items to virtualize
 * @param {number} itemHeight - Height of each item
 * @param {number} containerHeight - Height of the container
 * @returns {Object} Virtual scrolling utilities
 */
export function useVirtualScroll(items, itemHeight, containerHeight) {
  const [scrollTop, setScrollTop] = useState(0);
  
  const visibleCount = Math.ceil(containerHeight / itemHeight);
  const startIndex = Math.floor(scrollTop / itemHeight);
  const endIndex = Math.min(startIndex + visibleCount + 1, items.length);
  
  const visibleItems = items.slice(startIndex, endIndex);
  const totalHeight = items.length * itemHeight;
  const offsetY = startIndex * itemHeight;

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  return {
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll,
    startIndex,
    endIndex
  };
}